import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ElectionCategory } from '../../constants/categories';
import { EntryType, EntryStatus, RuleType } from '../../constants/commission';

export interface CommissionOrderRef {
  order_id: string;
  plan_name: string | null;
  // The category of the plan bought; null for a plan without a category (migration 0029)
  category: ElectionCategory | null;
  channel?: 'creator' | 'channel_partner';
  discount_percent?: string | null;
  promo_code: string;
  paid_at: string;
  status: string;
}

export interface CommissionHoldInfo {
  id: string;
  ledger_entry_id: string;
  source: 'manual' | 'auto_suspend';
  reason: string;
  placed_by_type: 'owner' | 'system';
  placed_by: string | null;
  placed_at: string;
  released_at: string | null;
  released_by: string | null;
  release_reason: string | null;
}

export interface CommissionEntryItem {
  id: string;
  entry_type: EntryType;
  // Signed decimal string: earned >= 0, reversal <= 0
  amount: string;
  // Derived when read: pending / held / available / paid
  status: EntryStatus;
  available_at: string;
  created_at: string;
  reversal_of_id: string | null;
  payout_id: string | null;
  basis: 'net' | 'gross';
  basis_amount: string;
  rule_type: RuleType;
  rule_value: string;
  order: CommissionOrderRef;
  rule_id: string;
  partner: { id: string; name: string; mobile: string };
  hold?: CommissionHoldInfo | null;
}

export interface CommissionTotalsBucket {
  entry_count: number;
  amount: string;
}

export interface CommissionTotals extends CommissionTotalsBucket {
  pending: CommissionTotalsBucket;
  held: CommissionTotalsBucket;
  available: CommissionTotalsBucket;
  paid: CommissionTotalsBucket;
}

export interface CommissionLedgerState {
  items: CommissionEntryItem[];
  totals: CommissionTotals;
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;
}

const ZERO_BUCKET: CommissionTotalsBucket = { entry_count: 0, amount: '0.00' };

const initialState: CommissionLedgerState = {
  items: [],
  totals: { ...ZERO_BUCKET, pending: ZERO_BUCKET, held: ZERO_BUCKET, available: ZERO_BUCKET, paid: ZERO_BUCKET },
  total: 0,
  limit: 25,
  offset: 0,
  loading: false,
  error: null,
};

const commissionLedgerSlice = createSlice({
  name: 'commissionLedger',
  initialState,
  reducers: {
    setCommissionLedgerLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setCommissionLedgerError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCommissionLedgerPage: (
      state,
      action: PayloadAction<{
        items: CommissionEntryItem[];
        totals: CommissionTotals;
        total: number;
        limit: number;
        offset: number;
      }>
    ) => {
      const { items, totals, total, limit, offset } = action.payload;
      state.items = items;
      state.totals = totals;
      state.total = total;
      state.limit = limit;
      state.offset = offset;
    },
  },
});

export const { setCommissionLedgerLoading, setCommissionLedgerError, setCommissionLedgerPage } =
  commissionLedgerSlice.actions;

export default commissionLedgerSlice.reducer;
